import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  type ReactNode,
} from 'react';

const PROCESSING_SECONDS_PER_RESUME = 20;
const PROCESSING_MILLISECONDS_PER_RESUME = PROCESSING_SECONDS_PER_RESUME * 1000;
const CLEANUP_TICK_MS = 1000;
const PROCESSING_QUEUE_STORAGE_KEY = 'hr.processingQueueByJobNotifications';

type ProcessingQueueMap = Record<string, number>;

type NotificationsContextValue = {
  markProcessingQueue: (jobId: string | number, filesCount: number) => void;
  markProcessingQueueForDuration: (jobId: string | number, durationMs: number) => void;
  isProcessingQueueActive: (jobId: string | number) => boolean;
  getProcessingQueueRemainingMs: (jobId: string | number) => number;
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

type NotificationsProviderProps = {
  children: ReactNode;
};

const cleanExpiredEntries = (input: ProcessingQueueMap, currentNow: number): ProcessingQueueMap => {
  const cleaned: ProcessingQueueMap = {};

  Object.entries(input).forEach(([jobId, activeUntil]) => {
    if (Number.isFinite(activeUntil) && activeUntil > currentNow) {
      cleaned[jobId] = activeUntil;
    }
  });

  return cleaned;
};

const getStoredProcessingQueueState = (): ProcessingQueueMap => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const storedValue = window.localStorage.getItem(PROCESSING_QUEUE_STORAGE_KEY);
    if (!storedValue) {
      return {};
    }

    const parsedValue = JSON.parse(storedValue) as unknown;
    if (!parsedValue || typeof parsedValue !== 'object') {
      window.localStorage.removeItem(PROCESSING_QUEUE_STORAGE_KEY);
      return {};
    }

    return cleanExpiredEntries(parsedValue as ProcessingQueueMap, Date.now());
  } catch {
    window.localStorage.removeItem(PROCESSING_QUEUE_STORAGE_KEY);
    return {};
  }
};

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const [processingQueueByJob, setProcessingQueueByJob] = useState<ProcessingQueueMap>(() =>
    getStoredProcessingQueueState()
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const persistedState = cleanExpiredEntries(processingQueueByJob, Date.now());
    if (Object.keys(persistedState).length === 0) {
      window.localStorage.removeItem(PROCESSING_QUEUE_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(PROCESSING_QUEUE_STORAGE_KEY, JSON.stringify(persistedState));
  }, [processingQueueByJob]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const currentNow = Date.now();

      setProcessingQueueByJob((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev;
        }

        setNow(currentNow);

        let hasExpiredJobs = false;
        const nextState: ProcessingQueueMap = {};

        Object.entries(prev).forEach(([jobId, activeUntil]) => {
          if (activeUntil > currentNow) {
            nextState[jobId] = activeUntil;
          } else {
            hasExpiredJobs = true;
          }
        });

        return hasExpiredJobs ? nextState : prev;
      });
    }, CLEANUP_TICK_MS);

    return () => window.clearInterval(interval);
  }, []);

  const markProcessingQueueForDuration = useCallback((jobId: string | number, durationMs: number) => {
    const safeDurationMs = Number.isFinite(durationMs) ? Math.floor(durationMs) : 0;
    if (safeDurationMs <= 0) {
      return;
    }

    const jobKey = String(jobId);
    const currentNow = Date.now();
    setNow(currentNow);

    setProcessingQueueByJob((prev) => {
      const currentActiveUntil = prev[jobKey] ?? 0;
      const baseline = Math.max(currentActiveUntil, currentNow);
      return {
        ...prev,
        [jobKey]: baseline + safeDurationMs,
      };
    });
  }, []);

  const markProcessingQueue = useCallback((jobId: string | number, filesCount: number) => {
    const safeFilesCount = Number.isFinite(filesCount) ? Math.floor(filesCount) : 0;
    if (safeFilesCount <= 0) {
      return;
    }

    markProcessingQueueForDuration(jobId, safeFilesCount * PROCESSING_MILLISECONDS_PER_RESUME);
  }, [markProcessingQueueForDuration]);

  const isProcessingQueueActive = useCallback(
    (jobId: string | number) => {
      const activeUntil = processingQueueByJob[String(jobId)] ?? 0;
      return activeUntil > now;
    },
    [now, processingQueueByJob]
  );

  const getProcessingQueueRemainingMs = useCallback(
    (jobId: string | number) => {
      const activeUntil = processingQueueByJob[String(jobId)] ?? 0;
      return Math.max(0, activeUntil - now);
    },
    [now, processingQueueByJob]
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      markProcessingQueue,
      markProcessingQueueForDuration,
      isProcessingQueueActive,
      getProcessingQueueRemainingMs,
    }),
    [getProcessingQueueRemainingMs, isProcessingQueueActive, markProcessingQueue, markProcessingQueueForDuration]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
}
